import {
  CASH_VALUES,
  cashBreakdown,
  cashLabel,
  describeCash,
  isBill,
  type CashCounts,
  type CashKind,
  type CashPile,
} from "../lib/money";

/* The signature element.
 *
 * A jar does not fill with a gradient -- it fills with the ACTUAL money that
 * makes up the balance. $7.50 draws as a five, two ones and two quarters, and
 * a five-year-old can count them. This is the clearest expression of the thing
 * the whole app is for: money is a handful of things, not a number that goes
 * up.
 *
 * Notes are what make that survive a real balance. Coins alone turned $7.50
 * into thirty identical discs -- a speckle nobody counts -- and anything past
 * MAX_DRAWN degraded to a list of multipliers. With notes in the mix a jar
 * holding twenty dollars is six objects.
 */

const MAX_DRAWN = 24;

/* How far each note lies off square once the pile has settled. Fixed per
 * denomination and not random: a jar redrawn mid-pour must not reshuffle its
 * contents between frames, and a jar redrawn after a spend must look like the
 * same pile with something taken out of it. */
const TILT: Partial<Record<CashKind, number>> = {
  hundred: -2.5,
  fifty: 2,
  twenty: -1.5,
  ten: 2.5,
  five: -2,
  one: 1.5,
};

const COIN_SIZES: Record<string, number> = {
  quarter: 26,
  nickel: 23,
  dime: 18,
  penny: 20,
};

/* Quarters, dimes and nickels are cupronickel -- silver, not gold. Only the
 * penny is copper. */
const IS_COPPER: Record<string, boolean> = {
  quarter: false,
  nickel: false,
  dime: false,
  penny: true,
};

export function Coin({ kind }: { kind: CashKind }) {
  const d = COIN_SIZES[kind] ?? 20;
  const copper = IS_COPPER[kind];
  return (
    <span
      aria-hidden="true"
      style={{
        width: d,
        height: d,
        borderRadius: "50%",
        display: "inline-grid",
        placeItems: "center",
        flex: "0 0 auto",
        background: copper
          ? "radial-gradient(circle at 34% 30%, #f0b784, var(--coin-gold-edge))"
          : "radial-gradient(circle at 34% 30%, #f8fafb, var(--coin-silver-edge))",
        border: `1.5px solid ${copper ? "#8f5a25" : "var(--coin-silver-edge)"}`,
        boxShadow: "0 1px 1px rgba(43,33,24,0.28)",
        fontFamily: "var(--font-num)",
        fontSize: d * 0.42,
        fontWeight: 700,
        color: copper ? "#6d3f16" : "var(--coin-silver-ink)",
        lineHeight: 1,
      }}
    >
      {CASH_VALUES[kind]}
    </span>
  );
}

/* A note.
 *
 * Every denomination is drawn at the SAME size, because every US note is the
 * same size. That is not a shortcut, it is the lesson: a twenty is worth more
 * than a one without being bigger than it, which is the first thing about
 * paper money that has to be taken on trust rather than seen.
 */
export function Bill({ kind, settled }: { kind: CashKind; settled?: boolean }) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: 44,
        height: 19,
        flex: "0 0 auto",
        /* Notes in a jar overlap and lie off square. Both are off by default:
           the counting panel draws the same money to BE counted, and a pile
           you cannot separate with your eye is the one thing that panel
           cannot afford. */
        transform: settled ? `rotate(${TILT[kind] ?? 0}deg)` : undefined,
        margin: settled ? "0 -3px" : undefined,
        display: "inline-grid",
        placeItems: "center",
        background: "var(--bill)",
        border: "1.5px solid var(--bill-edge)",
        /* The inset line is the printed frame every banknote has. Without it a
           note reads as a plain green tile. */
        boxShadow:
          "inset 0 0 0 1px rgba(255,255,255,0.55), inset 0 0 0 2.5px var(--bill)," +
          " 0 1px 1px rgba(43,33,24,0.28)",
        borderRadius: 3,
        fontFamily: "var(--font-num)",
        fontSize: 11,
        fontWeight: 700,
        color: "var(--bill-ink)",
        lineHeight: 1,
      }}
    >
      {cashLabel(kind)}
    </span>
  );
}

function Piece({ kind, settled }: { kind: CashKind; settled?: boolean }) {
  return isBill(kind) ? <Bill kind={kind} settled={settled} /> : <Coin kind={kind} />;
}

/* What is in the jar, drawn.
 *
 * `counts` overrides the fewest-pieces breakdown, which is what lets the
 * counting panel behind a tapped jar redraw the same total as the child moves
 * its sliders. Left out -- the shelf's case -- it is the fewest notes and
 * coins that make the amount.
 */
export function CashFill({
  cents,
  counts,
  settled = false,
}: {
  cents: number;
  counts?: CashCounts;
  /** Draw it as a pile that has been put down -- notes overlapping and off
   *  square -- rather than as separable pieces. On for the jars on the shelf,
   *  off for the counting panel behind a tapped one. */
  settled?: boolean;
}) {
  const piles: CashPile[] = counts
    ? (Object.keys(CASH_VALUES) as CashKind[])
        .filter((kind) => (counts[kind] ?? 0) > 0)
        .sort((a, b) => CASH_VALUES[b] - CASH_VALUES[a])
        .map((kind) => ({ kind, count: counts[kind] ?? 0 }))
    : cashBreakdown(cents);

  const total = piles.reduce((sum, p) => sum + p.count, 0);

  if (total === 0) {
    // A jar with money in it is a pile of money; a jar without should still
    // look like a jar waiting for one, not like the drawing broke.
    return (
      <div style={{ display: "grid", justifyItems: "center", gap: 5 }}>
        <span
          aria-hidden="true"
          style={{
            width: 24,
            height: 24,
            borderRadius: "50%",
            border: "2px dashed var(--ink-3)",
            opacity: 0.55,
          }}
        />
        <span style={{ color: "var(--ink-3)", fontSize: 12.5, fontWeight: 600 }}>
          empty
        </span>
      </div>
    );
  }

  if (total <= MAX_DRAWN) {
    /* wrap-reverse fills the BOTTOM line first, and the piles arrive biggest
       first, so the notes settle flat at the base of the jar with the change
       resting on top -- the way a jar of money actually sits. */
    return (
      <div
        style={{
          display: "flex",
          flexWrap: "wrap-reverse",
          gap: 3,
          justifyContent: "center",
          alignContent: "flex-start",
        }}
      >
        {piles.flatMap((pile) =>
          Array.from({ length: pile.count }, (_, i) => (
            <Piece key={`${pile.kind}-${i}`} kind={pile.kind} settled={settled} />
          )),
        )}
      </div>
    );
  }

  // Too many to count at a glance. One of each with a multiplier, the way you
  // would describe a big pile out loud rather than counting it.
  return (
    <div style={{ display: "grid", gap: 4, justifyItems: "center" }}>
      {piles.map((pile) => (
        <span
          key={pile.kind}
          style={{ display: "flex", alignItems: "center", gap: 5 }}
        >
          <Piece kind={pile.kind} />
          <span
            className="money"
            style={{ fontSize: 13, fontWeight: 700, color: "var(--ink-2)" }}
          >
            ×{pile.count}
          </span>
        </span>
      ))}
    </div>
  );
}

export function CashDescription({ cents }: { cents: number }) {
  return <span className="sr-only">{describeCash(cents)}</span>;
}
